import React, { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import { Button } from 'reactstrap';
import Sidebar from '../../Sidebar';
import { useNavigate, useParams } from 'react-router';
import { useCookies } from 'react-cookie';
import FloatingControls from '../../components/FloatingControls';
import FormPageLayout from '../../components/FormPageLayout';
import LoadingOverlay from '../../components/LoadingOverlay';
import ErrorToast from '../../ErrorToast';
import { displayError } from '../../helpers';
import { getUserAllowedPlants } from '../../services/PlantServices';
import { getAllEquipments } from '../../services/EquipmentServices';
import { getAllVendors } from '../../services/VendorServices';
import { getMaintenanceServiceDetails, updateMaintenanceService } from '../../services/MaintenanceServices';
import MaintenanceServiceForm from './MaintenanceServiceForm';
import {
    buildServiceFormData,
    emptySparePartLine,
    mapServiceRecordToForm,
} from './maintenanceServiceConstants';

function PreventiveMaintenanceEdit() {
    const { id } = useParams();
    const [cookies] = useCookies(['myToken']);
    const navigate = useNavigate();
    const [plants, setPlants] = useState([]);
    const [equipments, setEquipments] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [inProgress, setInProgress] = useState(false);
    const [isActive, setIsActive] = useState(true);
    const [loaded, setLoaded] = useState(false);
    const [inputs, setInputs] = useState({});
    const [spareParts, setSpareParts] = useState([emptySparePartLine(1)]);
    const [selectedFiles, setSelectedFiles] = useState({});
    const [clearFiles, setClearFiles] = useState({ bill: false, report: false });

    useEffect(() => {
        getUserAllowedPlants(cookies).then((response) => {
            setPlants((response.plant_list || []).filter((p) => p.status).map((p) => ({ value: p.id, label: p.plant_alias })));
        });
        getAllEquipments(cookies).then((response) => {
            setEquipments((response.equipment_list || []).filter((e) => e.status === true || e.status === 1 || e.status === 'Active'));
        });
        getAllVendors(cookies).then((response) => {
            setVendors(response.vendor_list || response.vendors || []);
        });
    }, [cookies]);

    useEffect(() => {
        if (!id) return;
        setInProgress(true);
        getMaintenanceServiceDetails(cookies, id)
            .then((data) => {
                setIsActive(data.status === true || data.status === 1 || data.status === 'Active');
                setInputs(mapServiceRecordToForm(data));
                setSpareParts(data.spare_parts_list?.length ? data.spare_parts_list : [emptySparePartLine(1)]);
                setLoaded(true);
            })
            .catch((error) => displayError(error?.response?.data || error, 'Load Failed'))
            .finally(() => setInProgress(false));
    }, [cookies, id]);

    const handleSubmit = (event) => {
        event.preventDefault();
        if (!isActive) return;
        setInProgress(true);
        const payload = buildServiceFormData(inputs, spareParts, selectedFiles, {
            clear_bill_file: clearFiles.bill,
            clear_service_report_file: clearFiles.report,
        });
        updateMaintenanceService(cookies, id, payload)
            .then(() => Swal.fire({ icon: 'success', title: 'Service record updated' }))
            .catch((error) => displayError(error?.response?.data || error, 'Update Failed'))
            .finally(() => setInProgress(false));
    };

    return (
        <div id="outer-container" className="App">
            <Sidebar pageWrapId="page-wrap" outerContainerId="outer-container" />
            <ErrorToast />
            <LoadingOverlay active={inProgress} />
            <div id="page-wrap">
                <form onSubmit={handleSubmit}>
                    <FloatingControls tableLink="/PreventiveMaintenanceTable" formLink="/PreventiveMaintenance" addNewLabel="Main Form" enableCancel onCancel={() => navigate('/PreventiveMaintenanceTable')} />
                    <FormPageLayout moduleLabel="MAINTENANCE" title="Service & Breakdown Record" subtitle="Update preventive schedule, breakdown notes, spare parts and uploaded documents." modeLabel={isActive ? 'UPDATE MODE' : 'CANCELLED'}>
                        {loaded && (
                            <MaintenanceServiceForm cookies={cookies} plants={plants} equipments={equipments} vendors={vendors} inputs={inputs} setInputs={setInputs} spareParts={spareParts} setSpareParts={setSpareParts} selectedFiles={selectedFiles} setSelectedFiles={setSelectedFiles} clearFiles={clearFiles} setClearFiles={setClearFiles} readOnly={!isActive} />
                        )}
                        {isActive && (
                            <div className="text-right mb-4 px-3">
                                <Button color="primary" type="submit">Update Service Record</Button>
                            </div>
                        )}
                    </FormPageLayout>
                </form>
            </div>
        </div>
    );
}

export default PreventiveMaintenanceEdit;
